// DOM helpers for the lobby and room screens. Stays vanilla; no framework.

import { attachRemoteStream, observeMicLevel, observeSpeaking, type AudioDevice } from "./audio.ts";
import { renderMarkdown } from "./markdown.ts";
import { MAX_NAME_LEN } from "./wire.ts";

export interface PeerRowOptions {
  id: string;
  name: string;
  // onMuteToggle fires with the new muted state when the user clicks the
  // mute button. Mutes playback of this peer's audio locally.
  onMuteToggle: (muted: boolean) => void;
  // onKickRequest fires after the user confirms removing this peer. The
  // confirm prompt lives inside the row; consumers just dispatch the kick.
  onKickRequest: () => void;
}

export interface PeerRowHandle {
  id: string;
  el: HTMLLIElement;
  setName: (n: string) => void;
  setSpeaking: (s: boolean) => void;
  setQuality: (q: "good" | "degraded" | "poor" | "unknown") => void;
  setTransport: (t: "direct" | "relayed" | "unknown") => void;
  setMuted: (m: boolean) => void;
  setCipherHealth: (healthy: boolean) => void;
  attach: (stream: MediaStream) => void;
  destroy: () => void;
}

export interface SelfBlockOptions {
  name: string;
  onMuteToggle: (muted: boolean) => void;
  onRenameRequest: (name: string) => void;
  onLeaveRequest: () => void;
}

export interface SelfBlockHandle {
  setName: (n: string) => void;
  setSpeaking: (s: boolean) => void;
  setMuted: (m: boolean) => void;
  setQuality: (q: "good" | "degraded" | "poor" | "unknown") => void;
  reset: () => void;
}

export function $(sel: string): HTMLElement {
  const el = document.querySelector(sel);
  if (!el) throw new Error(`element not found: ${sel}`);
  return el as HTMLElement;
}

export function showLobby(): void {
  ($("#lobby") as HTMLElement).hidden = false;
  ($("#room") as HTMLElement).hidden = true;
  document.body.classList.remove("in-room");
  const codeNarrow = document.querySelector<HTMLElement>("#room-code-narrow");
  if (codeNarrow) codeNarrow.textContent = "";
  const u = new URL(location.href);
  if (u.searchParams.has("room")) {
    u.searchParams.delete("room");
    history.replaceState(null, "", u.pathname + u.search);
  }
}

export function showRoom(code: string): void {
  ($("#lobby") as HTMLElement).hidden = true;
  ($("#room") as HTMLElement).hidden = false;
  ($("#room-code") as HTMLElement).textContent = code;
  document.body.classList.add("in-room");
  // The narrow site-header carries the room code (the rail-head is hidden
  // on narrow). Mirror it.
  const codeNarrow = document.querySelector<HTMLElement>("#room-code-narrow");
  if (codeNarrow) codeNarrow.textContent = code;
  // pushState so Back returns to the lobby. Skip when the URL already
  // matches (initial load via ?room=, or re-entry from a popstate handler).
  const u = new URL(location.href);
  if (u.searchParams.get("room") !== code) {
    u.searchParams.set("room", code);
    history.pushState(null, "", u.pathname + u.search);
  }
}

export function setLobbyError(msg: string | null): void {
  const el = $("#lobby-error") as HTMLElement;
  el.textContent = msg ?? "";
  el.hidden = !msg;
}

// The rail meta line carries either the current peer count ("4 peers") or
// a transient status ("Connecting…", "Reconnecting (attempt 2)…", "Link
// copied"). Status takes precedence; clearing status restores peer count.
let currentStatus = "";
let peerCount = 0;
function renderRailMeta(): void {
  // We mirror the meta into both the rail-head (wide) and the site-header
  // (narrow), so collect both elements once and apply the same logic.
  const els = [
    $("#room-status") as HTMLElement,
    document.querySelector<HTMLElement>("#room-status-narrow"),
  ].filter((x): x is HTMLElement => x !== null);
  for (const el of els) {
    el.classList.remove("is-count", "is-status");
    if (currentStatus) {
      el.textContent = currentStatus;
      el.classList.add("is-status");
      continue;
    }
    if (peerCount <= 0) { el.textContent = ""; continue; }
    el.textContent = peerCount === 1 ? "1 user" : `${peerCount} users`;
    // The count form is suppressed on narrow (the Users tab carries it).
    el.classList.add("is-count");
  }
}
export function setRoomStatus(msg: string): void {
  currentStatus = msg;
  renderRailMeta();
}
export function setPeerCount(n: number): void {
  peerCount = n;
  renderRailMeta();
  // Mirror the count into the Users tab label (visible on narrow viewports).
  const countEl = document.querySelector<HTMLElement>("#tab-users-count");
  if (countEl) countEl.textContent = n > 0 ? String(n) : "";
}

export function shareLink(code: string): string {
  const u = new URL(location.href);
  u.searchParams.set("room", code);
  return u.toString();
}

// fillDeviceSelect populates a <select> with audio devices, preserving
// the currently-selected ID where possible. "" maps to the system default,
// which is rendered as a leading "(system default)" option.
export function fillDeviceSelect(
  sel: HTMLSelectElement,
  devices: AudioDevice[],
  selected: string,
): void {
  const prev = selected || sel.value || "";
  sel.replaceChildren();
  const dflt = document.createElement("option");
  dflt.value = "";
  dflt.textContent = "(system default)";
  sel.appendChild(dflt);
  for (const d of devices) {
    const opt = document.createElement("option");
    opt.value = d.deviceId;
    opt.textContent = d.label;
    sel.appendChild(opt);
  }
  // Restore selection if the device is still present; otherwise keep default.
  if (devices.some((d) => d.deviceId === prev)) {
    sel.value = prev;
  } else {
    sel.value = "";
  }
}

// makePeerRow builds a row for a remote peer. Self lives in the rail-self
// block instead — see bindSelfBlock.
export function makePeerRow(opts: PeerRowOptions): PeerRowHandle {
  const tpl = document.querySelector<HTMLTemplateElement>("#peer-row");
  if (!tpl) throw new Error("peer-row template missing");
  const node = tpl.content.firstElementChild!.cloneNode(true) as HTMLLIElement;
  const nameEl = node.querySelector<HTMLElement>(".nm")!;
  const transportEl = node.querySelector<HTMLElement>(".transport")!;
  const cipherEl = node.querySelector<HTMLElement>(".cipher")!;
  const volEl = node.querySelector<HTMLInputElement>(".vol")!;
  const muteBtn = node.querySelector<HTMLButtonElement>(".mute")!;
  const kickBtn = node.querySelector<HTMLButtonElement>(".kick")!;

  nameEl.textContent = opts.name;

  let stopVAD: (() => void) | null = null;
  let detachAudio: (() => void) | null = null;
  let setVolumeFn: ((v: number) => void) | null = null;
  let currentQuality: "good" | "degraded" | "poor" | "unknown" = "unknown";

  const handle: PeerRowHandle = {
    id: opts.id,
    el: node,
    setName: (n) => { nameEl.textContent = n; },
    setSpeaking: (s) => node.classList.toggle("speaking", s),
    setQuality: (q) => {
      currentQuality = q;
      // Quality is implicit in normal operation; we only flag degraded /
      // poor by tinting the badge frame so the row stays calm at rest.
      node.classList.toggle("q-degraded", q === "degraded");
      node.classList.toggle("q-poor", q === "poor");
    },
    setTransport: (t) => {
      transportEl.classList.remove("relayed");
      if (t === "unknown") {
        transportEl.textContent = "";
        transportEl.title = "";
        return;
      }
      if (t === "relayed") transportEl.classList.add("relayed");
      transportEl.textContent = t === "relayed" ? "relay" : "wss";
      transportEl.title = t === "direct" ? "peer-to-peer" : "via TURN relay";
    },
    setMuted: (m) => {
      node.classList.toggle("is-muted", m);
      const label = m ? "Unmute" : "Mute";
      muteBtn.title = label;
      muteBtn.setAttribute("aria-label", label);
    },
    setCipherHealth: (healthy) => {
      if (healthy) {
        cipherEl.textContent = "";
        cipherEl.title = "";
        cipherEl.classList.remove("bad");
      } else {
        cipherEl.textContent = "⚠";
        cipherEl.title = "couldn't decrypt audio from this peer";
        cipherEl.classList.add("bad");
      }
    },
    attach: (s) => {
      // Close any prior playback / VAD so a re-attach (e.g. after the room
      // reconnects and a fresh ontrack fires for the same peer) doesn't
      // leak the previous audio element or analyser.
      detachAudio?.();
      stopVAD?.();
      const remote = attachRemoteStream(s);
      detachAudio = remote.close;
      setVolumeFn = remote.setVolume;
      const audioTrack = s.getAudioTracks()[0];
      if (audioTrack) {
        stopVAD = observeSpeaking(audioTrack, (speaking) => handle.setSpeaking(speaking));
      }
      remote.setVolume(Number(volEl.value) / 100);
    },
    destroy: () => {
      stopVAD?.();
      detachAudio?.();
      node.remove();
    },
  };

  muteBtn.addEventListener("click", () => {
    const next = !node.classList.contains("is-muted");
    handle.setMuted(next);
    opts.onMuteToggle(next);
  });
  volEl.addEventListener("input", () => {
    setVolumeFn?.(Number(volEl.value) / 100);
  });
  // Kick is consequential and discoverable (destructive icon next to mute),
  // so a single confirm step is enough — the kicked peer can rejoin if they
  // still have the code, and every kick is broadcast as a system line so
  // accidents are visible in chat. The popover mirrors the chat-delete
  // confirm: red checkmark anchored under the kick button. Click again to
  // toggle closed; outside-click / Esc handled by bindChatMessageActions.
  // stopPropagation prevents the document-level outside-click handler from
  // immediately closing the popover this very click just opened.
  kickBtn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    openKickConfirm(kickBtn, () => opts.onKickRequest());
  });

  void currentQuality;
  return handle;
}

export function appendPeerRow(handle: PeerRowHandle): void {
  ($("#peers") as HTMLElement).appendChild(handle.el);
}

// bindSelfBlock hooks up the static self-only chrome — three places that
// reflect the same person:
//   - rail-self (wide footer): name + quality + mic + (no leave; that's
//     in the leave-btn next to mic on the wide rail-self too)
//   - site-header narrow controls: glyph + room code + mic + share + gear
//   - self peer row at the top of the Users list (narrow only): name with
//     click-to-rename + leave button
// All three update in sync; clicking any mic toggles the same mic state.
export function bindSelfBlock(opts: SelfBlockOptions): SelfBlockHandle {
  const nameEl = $("#self-name");
  const qmetaEl = $("#self-quality");
  const muteBtn = $("#self-mute") as HTMLButtonElement;
  const muteBtnNarrow = document.querySelector<HTMLButtonElement>("#self-mute-narrow");
  const peerRowName = document.querySelector<HTMLElement>("#self-peer-name");
  const railSelf = document.querySelector<HTMLElement>(".rail-self");
  const peerRow = document.querySelector<HTMLElement>("#self-peer-row");

  let currentName = opts.name;
  nameEl.textContent = currentName;
  if (peerRowName) peerRowName.textContent = currentName;
  qmetaEl.textContent = "";
  qmetaEl.classList.remove("degraded", "poor");
  muteBtn.classList.remove("is-muted");
  muteBtnNarrow?.classList.remove("is-muted");

  const handle: SelfBlockHandle = {
    setName: (n) => {
      currentName = n;
      // If a rename input is open in either spot, leave it alone.
      if (!nameEl.querySelector("input")) nameEl.textContent = n;
      if (peerRowName && !peerRowName.querySelector("input")) peerRowName.textContent = n;
    },
    setSpeaking: (s) => {
      railSelf?.classList.toggle("speaking", s);
      peerRow?.classList.toggle("speaking", s);
    },
    setMuted: (m) => {
      muteBtn.classList.toggle("is-muted", m);
      muteBtnNarrow?.classList.toggle("is-muted", m);
      const label = m ? "Unmute" : "Mute";
      for (const b of [muteBtn, muteBtnNarrow]) {
        if (!b) continue;
        b.title = label;
        b.setAttribute("aria-label", label);
      }
    },
    setQuality: (q) => {
      qmetaEl.classList.remove("degraded", "poor");
      if (q === "good") qmetaEl.textContent = "good";
      else if (q === "degraded") { qmetaEl.textContent = "degraded"; qmetaEl.classList.add("degraded"); }
      else if (q === "poor") { qmetaEl.textContent = "poor"; qmetaEl.classList.add("poor"); }
      else qmetaEl.textContent = "";
      if (qmetaEl.textContent) {
        qmetaEl.dataset.tip = "Outgoing audio quality as measured from others in the room.";
      } else {
        delete qmetaEl.dataset.tip;
      }
    },
    reset: () => {
      muteBtn.classList.remove("is-muted");
      muteBtnNarrow?.classList.remove("is-muted");
      qmetaEl.textContent = "";
      delete qmetaEl.dataset.tip;
      qmetaEl.classList.remove("degraded", "poor");
      railSelf?.classList.remove("speaking");
      peerRow?.classList.remove("speaking");
      nameEl.textContent = "";
      if (peerRowName) peerRowName.textContent = "";
    },
  };

  // Mute toggle: same handler regardless of which mic button was clicked.
  const onMuteClick = () => {
    const next = !muteBtn.classList.contains("is-muted");
    handle.setMuted(next);
    opts.onMuteToggle(next);
  };
  muteBtn.addEventListener("click", onMuteClick);
  muteBtnNarrow?.addEventListener("click", onMuteClick);

  // Click-to-rename. Wired on both name elements; the server's peer_renamed
  // broadcast is the source of truth for the displayed name.
  const wireRename = (target: HTMLElement) => {
    target.addEventListener("click", () => {
      if (target.querySelector("input")) return;
      startEdit(target);
    });
  };
  wireRename(nameEl);
  if (peerRowName) wireRename(peerRowName);

  // Leave button in the narrow site-header. The handler is the same as
  // the wide rail-self leave (and the brand-link Back behavior); main.ts
  // owns the actual leaveRoom call via opts.onLeaveRequest.
  document.querySelector<HTMLButtonElement>("#self-leave-narrow")
    ?.addEventListener("click", () => opts.onLeaveRequest());

  function startEdit(target: HTMLElement): void {
    const input = document.createElement("input");
    input.type = "text";
    input.value = currentName;
    input.maxLength = MAX_NAME_LEN;
    input.className = "name-edit";
    target.replaceChildren(input);
    input.focus();
    input.select();
    let committed = false;
    const commit = () => {
      if (committed) return;
      committed = true;
      const next = input.value.trim();
      if (next && next !== currentName) {
        opts.onRenameRequest(next);
      }
      target.textContent = currentName;
    };
    const cancel = () => {
      if (committed) return;
      committed = true;
      target.textContent = currentName;
    };
    input.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter") { ev.preventDefault(); commit(); }
      else if (ev.key === "Escape") { ev.preventDefault(); cancel(); }
    });
    input.addEventListener("blur", () => commit());
  }

  return handle;
}

// formatTime renders a sender timestamp as HH:MM in the viewer's locale.
// Seconds are dropped — chat in this app is small enough that minute-level
// precision matches what the rest of the UI promises.
function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

// appendChatMessage renders a message at the end of the chat scroll. There
// is no visual difference between self and peer messages — the user knows
// the name they're sending under, and the rail-bottom self-row anchors that.
//
// `from` is the author's peer ID (localId for self). It is stamped on the
// node as a data attribute so renameChatAuthor can rewrite history when
// a peer renames, and so consecutive messages from the same author can be
// detected and rendered without repeating the name + time. `id` is the
// sender-generated message id used to address later edits/deletes.
export function appendChatMessage(opts: {
  id: string;
  from: string;
  isSelf: boolean;
  name: string;
  body: string;
  ts: number;
}): void {
  const tpl = document.querySelector<HTMLTemplateElement>("#chat-message");
  if (!tpl) throw new Error("chat-message template missing");
  const node = tpl.content.firstElementChild!.cloneNode(true) as HTMLLIElement;
  node.dataset.id = opts.id;
  node.dataset.from = opts.from;
  if (opts.isSelf) node.classList.add("own");
  const who = node.querySelector<HTMLElement>(".who")!;
  who.textContent = opts.name;
  who.classList.add(`who-c${authorColorIndex(opts.from)}`);
  node.querySelector<HTMLElement>(".time")!.textContent = formatTime(opts.ts);
  const bodyEl = node.querySelector<HTMLElement>(".body")!;
  // dataset.source stashes the original markdown so the inline editor and
  // later edits can round-trip without trying to recover the source from
  // the rendered HTML.
  bodyEl.dataset.source = opts.body;
  bodyEl.innerHTML = renderMarkdown(opts.body);
  // The kebab button is only meaningful on own messages; remote rows leave
  // it hidden. CSS gates the visibility further (hover-reveal on pointer
  // devices) and a long-press handler opens the menu on touch.
  if (opts.isSelf) {
    const btn = node.querySelector<HTMLButtonElement>(".msg-menu-btn");
    if (btn) btn.hidden = false;
  }
  appendChatNode(node);
}

// editChatMessage rewrites the body of a previously-rendered message and
// appends a small "(edited)" tag at the end of the body line. No-op if
// the id was never rendered (the recipient may have joined after the
// original send) or the message was already deleted.
export function editChatMessage(id: string, body: string, _editedTs: number): void {
  const list = document.querySelector<HTMLElement>("#chat-messages");
  if (!list) return;
  const node = list.querySelector<HTMLElement>(`.msg[data-id="${cssEscape(id)}"]`);
  if (!node || node.classList.contains("deleted")) return;
  const bodyEl = node.querySelector<HTMLElement>(".body");
  if (bodyEl) {
    bodyEl.dataset.source = body;
    bodyEl.innerHTML = renderMarkdown(body);
  }
  ensureEditedTag(node);
}

// deleteChatMessage replaces the message body with a tombstone and removes
// the kebab affordance. The row stays in place so author grouping for
// subsequent messages doesn't shift.
export function deleteChatMessage(id: string): void {
  const list = document.querySelector<HTMLElement>("#chat-messages");
  if (!list) return;
  const node = list.querySelector<HTMLElement>(`.msg[data-id="${cssEscape(id)}"]`);
  if (!node) return;
  node.classList.add("deleted");
  const bodyEl = node.querySelector<HTMLElement>(".body");
  if (bodyEl) bodyEl.textContent = "(message deleted)";
  node.querySelector<HTMLElement>(".msg-edited-tag")?.remove();
  node.querySelector<HTMLButtonElement>(".msg-menu-btn")?.remove();
  closeMessageMenu();
}

function ensureEditedTag(node: HTMLElement): void {
  if (node.querySelector(".msg-edited-tag")) return;
  const tag = document.createElement("span");
  tag.className = "msg-edited-tag";
  tag.textContent = "(edited)";
  // Append inside .head-line so the tag flows with the body text and
  // wraps with it on narrow widths. Keeping it out of the .msg grid (which
  // is body / time, two columns) avoids forcing the time onto its own row.
  const head = node.querySelector<HTMLElement>(".head-line");
  if (head) head.appendChild(tag);
}

// Single live popover and its anchor. We only ever show one menu at a time,
// so a module-level handle is simpler than per-row state. openMenuAnchor is
// the button that summoned the popover (kebab for chat-message menus, kick
// button for the kick-confirm popover); we keep it so closeMessageMenu can
// reset its aria-expanded regardless of which feature opened the popover.
interface ChatActionHandlers {
  onDelete: (id: string) => boolean;
}
let chatActionHandlers: ChatActionHandlers | null = null;
let openMenuRow: HTMLElement | null = null;
let openMenuEl: HTMLElement | null = null;
let openMenuAnchor: HTMLElement | null = null;
const LONG_PRESS_MS = 500;
const LONG_PRESS_TOLERANCE_PX = 8;

// bindChatMessageActions installs the kebab + long-press handlers on the
// chat list. Idempotent — safe to call once at startup.
export function bindChatMessageActions(handlers: ChatActionHandlers): void {
  chatActionHandlers = handlers;
  const list = document.querySelector<HTMLElement>("#chat-messages");
  if (!list) return;

  list.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const btn = target.closest<HTMLElement>(".msg-menu-btn");
    if (!btn) return;
    const row = btn.closest<HTMLElement>(".msg.own");
    if (!row) return;
    ev.stopPropagation();
    if (openMenuRow === row) {
      closeMessageMenu();
      return;
    }
    openMessageMenu(row);
  });

  // Long-press: works for any pointer type but in practice only fires for
  // touch — mouse users see the kebab on hover and tap-and-hold is usually
  // text selection on desktop.
  let pressTimer: number | null = null;
  let pressRow: HTMLElement | null = null;
  let pressX = 0;
  let pressY = 0;
  let suppressClick = false;

  const cancelPress = () => {
    if (pressTimer !== null) {
      window.clearTimeout(pressTimer);
      pressTimer = null;
    }
    pressRow = null;
  };

  list.addEventListener("pointerdown", (ev) => {
    if (ev.pointerType !== "touch") return;
    const target = ev.target as HTMLElement | null;
    const row = target?.closest<HTMLElement>(".msg.own") ?? null;
    if (!row) return;
    // Don't long-press if the user is interacting with the editor or the
    // kebab button itself — those have their own click semantics.
    if (target?.closest(".msg-menu-btn, .msg-editor")) return;
    cancelPress();
    pressRow = row;
    pressX = ev.clientX;
    pressY = ev.clientY;
    pressTimer = window.setTimeout(() => {
      pressTimer = null;
      if (!pressRow) return;
      suppressClick = true;
      openMessageMenu(pressRow);
      pressRow = null;
    }, LONG_PRESS_MS);
  });
  list.addEventListener("pointermove", (ev) => {
    if (!pressRow) return;
    const dx = ev.clientX - pressX;
    const dy = ev.clientY - pressY;
    if (dx * dx + dy * dy > LONG_PRESS_TOLERANCE_PX * LONG_PRESS_TOLERANCE_PX) cancelPress();
  });
  list.addEventListener("pointerup", cancelPress);
  list.addEventListener("pointercancel", cancelPress);
  // Suppress the synthetic click that follows a long-press so it doesn't
  // immediately re-toggle the menu we just opened.
  list.addEventListener(
    "click",
    (ev) => {
      if (suppressClick) {
        suppressClick = false;
        ev.preventDefault();
        ev.stopPropagation();
      }
    },
    true,
  );

  // Outside-click and Esc dismiss the menu. The list's own scroll also
  // closes it — recomputing the popover's position mid-scroll is fiddly
  // and the user can just reopen it.
  document.addEventListener("click", (ev) => {
    if (!openMenuEl) return;
    if (openMenuEl.contains(ev.target as Node)) return;
    closeMessageMenu();
  });
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && openMenuEl) {
      ev.stopPropagation();
      closeMessageMenu();
    }
  });
  list.addEventListener("scroll", () => {
    if (openMenuEl) closeMessageMenu();
  });
  window.addEventListener("resize", () => {
    if (openMenuEl) closeMessageMenu();
  });
}

function openMessageMenu(row: HTMLElement): void {
  closeMessageMenu();
  const tpl = document.querySelector<HTMLTemplateElement>("#chat-msg-menu");
  if (!tpl) return;
  const menu = tpl.content.firstElementChild!.cloneNode(true) as HTMLElement;
  document.body.appendChild(menu);
  openMenuEl = menu;
  openMenuRow = row;
  const btn = row.querySelector<HTMLButtonElement>(".msg-menu-btn");
  openMenuAnchor = btn ?? null;
  btn?.setAttribute("aria-expanded", "true");

  // For the regular menu, align the right edge with the kebab so it
  // doesn't bleed off the right side of the chat column.
  positionMenuRelativeTo(menu, btn ?? row, "right");

  menu.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    const item = target?.closest<HTMLElement>(".msg-menu-item");
    if (!item) return;
    ev.stopPropagation();
    const action = item.dataset.action;
    const id = row.dataset.id ?? "";
    if (!id) {
      closeMessageMenu();
      return;
    }
    if (action === "edit") {
      closeMessageMenu();
      enterComposerEditMode(row, id);
    } else if (action === "delete") {
      // Morph the popover into a confirm step so the user doesn't have to
      // move the cursor across the row to act on the prompt.
      showDeleteConfirmInMenu(menu, row, id);
    }
  });

  // Focus the first item so keyboard users can act on it immediately.
  menu.querySelector<HTMLButtonElement>(".msg-menu-item")?.focus();
  // Arrow-key navigation between items.
  menu.addEventListener("keydown", (ev) => {
    const items = Array.from(menu.querySelectorAll<HTMLButtonElement>(".msg-menu-item"));
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    if (ev.key === "ArrowDown") {
      ev.preventDefault();
      items[(idx + 1 + items.length) % items.length]?.focus();
    } else if (ev.key === "ArrowUp") {
      ev.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    }
  });
}

// positionMenuRelativeTo places `menu` (already attached to <body> with
// position: fixed) below `anchor`, with the popover's horizontal alignment
// chosen by `align`: "right" lines up the popover's right edge with the
// anchor's right edge (good for a multi-item menu under a top-right
// kebab); "center" centers the popover on the anchor (good for a small
// confirm popover sitting directly under the kebab).
function positionMenuRelativeTo(
  menu: HTMLElement,
  anchor: Element,
  align: "right" | "center",
): void {
  const aRect = anchor.getBoundingClientRect();
  const mRect = menu.getBoundingClientRect();
  const margin = 8;
  let left =
    align === "center"
      ? aRect.left + aRect.width / 2 - mRect.width / 2
      : aRect.right - mRect.width;
  let top = aRect.bottom + 4;
  if (left < margin) left = margin;
  if (left + mRect.width > window.innerWidth - margin) {
    left = window.innerWidth - mRect.width - margin;
  }
  if (top + mRect.height > window.innerHeight - margin) {
    // Flip above the anchor if there's no room below.
    top = aRect.top - mRect.height - 4;
    if (top < margin) top = margin;
  }
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}

function closeMessageMenu(): void {
  openMenuEl?.remove();
  openMenuEl = null;
  if (openMenuAnchor) {
    openMenuAnchor.setAttribute("aria-expanded", "false");
    openMenuAnchor = null;
  }
  openMenuRow = null;
}

// ---- Composer (send + edit) ------------------------------------------------
//
// The chat composer is the bottom textarea + Send button. We bind it once at
// startup via bindChatComposer; it routes plain submits to onSend and, while
// in "edit mode", routes them to onEditSave for the message currently being
// edited. Picking Edit on a row's kebab menu enters edit mode by stashing
// any current draft, prefilling the composer with the message's markdown
// source, and showing a small banner with a Cancel button. Esc cancels;
// committing or cancelling restores the prior draft.

interface ChatComposerHandlers {
  // onSend dispatches a fresh message. Returns true if accepted (clears
  // the composer); false to leave the draft in place.
  onSend: (text: string) => boolean;
  // onEditSave broadcasts an edit of `id`. Same return semantics.
  onEditSave: (id: string, text: string) => boolean;
}
let composerHandlers: ChatComposerHandlers | null = null;
let composerEditState: { id: string; originalDraft: string; row: HTMLElement } | null = null;

export function bindChatComposer(handlers: ChatComposerHandlers): void {
  composerHandlers = handlers;
  const chatInput = document.querySelector<HTMLTextAreaElement>("#chat-input");
  const form = document.querySelector<HTMLFormElement>("#chat-form");
  const cancelBtn = document.querySelector<HTMLButtonElement>("#chat-edit-cancel");
  if (!chatInput || !form) return;

  // Auto-grow the textarea as the user types. The CSS max-height caps it
  // so a long paste starts scrolling in place instead of pushing the
  // message list off-screen.
  const autosize = (): void => {
    chatInput.style.height = "auto";
    chatInput.style.height = `${chatInput.scrollHeight}px`;
  };
  chatInput.addEventListener("input", autosize);
  chatInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && composerEditState) {
      ev.preventDefault();
      cancelComposerEdit();
      return;
    }
    if (ev.key !== "Enter" || ev.isComposing) return;
    if (ev.shiftKey || ev.metaKey || ev.ctrlKey || ev.altKey) return;
    ev.preventDefault();
    form.requestSubmit();
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const text = chatInput.value;
    if (!text.trim()) return;
    if (composerEditState) {
      const editing = composerEditState;
      const ok = composerHandlers?.onEditSave(editing.id, text) ?? false;
      if (!ok) return;
      finishComposerEdit(editing.originalDraft);
      autosize();
      return;
    }
    if (composerHandlers?.onSend(text)) {
      chatInput.value = "";
      autosize();
    }
  });

  cancelBtn?.addEventListener("click", () => cancelComposerEdit());
}

function enterComposerEditMode(row: HTMLElement, id: string): void {
  const bodyEl = row.querySelector<HTMLElement>(".body");
  if (!bodyEl) return;
  const original = bodyEl.dataset.source ?? bodyEl.textContent ?? "";
  const chatInput = document.querySelector<HTMLTextAreaElement>("#chat-input");
  if (!chatInput) return;

  // If we were already editing a different message, restore that one's
  // draft before swapping in the new edit. The row's `.editing` class
  // also moves with us.
  const priorDraft = composerEditState ? composerEditState.originalDraft : chatInput.value;
  composerEditState?.row.classList.remove("editing");

  composerEditState = { id, originalDraft: priorDraft, row };
  row.classList.add("editing");

  chatInput.value = original;
  // Trigger autosize via the input event handler.
  chatInput.dispatchEvent(new Event("input", { bubbles: true }));
  setEditBannerVisible(true);
  updateComposerSubmitLabel(true);

  chatInput.focus();
  chatInput.setSelectionRange(chatInput.value.length, chatInput.value.length);
}

function cancelComposerEdit(): void {
  if (!composerEditState) return;
  finishComposerEdit(composerEditState.originalDraft);
}

function finishComposerEdit(restoreValue: string): void {
  const state = composerEditState;
  composerEditState = null;
  state?.row.classList.remove("editing");
  setEditBannerVisible(false);
  updateComposerSubmitLabel(false);
  const chatInput = document.querySelector<HTMLTextAreaElement>("#chat-input");
  if (!chatInput) return;
  chatInput.value = restoreValue;
  chatInput.dispatchEvent(new Event("input", { bubbles: true }));
}

function setEditBannerVisible(visible: boolean): void {
  const banner = document.querySelector<HTMLElement>("#chat-edit-banner");
  if (banner) banner.hidden = !visible;
}

function updateComposerSubmitLabel(editing: boolean): void {
  const submit = document.querySelector<HTMLButtonElement>("#chat-form button[type=\"submit\"]");
  if (submit) submit.textContent = editing ? "Save" : "Send";
}

// showDeleteConfirmInMenu replaces the menu's items with a single checkmark
// confirm button positioned directly below the kebab. To bail, the user
// clicks outside or hits Esc — both wired up at the document level. Enter
// also confirms.
function showDeleteConfirmInMenu(menu: HTMLElement, row: HTMLElement, id: string): void {
  if (row.classList.contains("deleted")) return;
  menu.classList.add("msg-menu-confirm");
  menu.replaceChildren();

  const yes = document.createElement("button");
  yes.type = "button";
  yes.className = "msg-menu-icon-btn msg-menu-icon-btn-danger";
  yes.setAttribute("aria-label", "Confirm delete");
  yes.title = "Confirm delete";
  yes.innerHTML = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polyline points="5 12 10 17 19 7" />
    </svg>`;
  menu.append(yes);

  const submit = () => {
    const ok = chatActionHandlers?.onDelete(id) ?? false;
    if (!ok) closeMessageMenu();
    // Success: room fires onChatDeleted → deleteChatMessage replaces the
    // body with a tombstone and closes the menu.
  };
  yes.addEventListener("click", (ev) => {
    ev.stopPropagation();
    submit();
  });
  // Enter confirms even if focus drifted off the button. Esc still closes
  // via the document-level handler in bindChatMessageActions.
  menu.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && !ev.isComposing) {
      ev.preventDefault();
      ev.stopPropagation();
      submit();
    }
  });

  // Reposition: the popover is now much smaller than the original menu,
  // so center it on the kebab to put the checkmark directly below it.
  const btn = row.querySelector<HTMLButtonElement>(".msg-menu-btn");
  if (btn) positionMenuRelativeTo(menu, btn, "center");
  yes.focus();
}

// openKickConfirm shows the same red-checkmark popover used for chat
// delete, anchored under the kick button. Cancellation routes through the
// document-level outside-click / Esc handlers wired in
// bindChatMessageActions; clicking the anchor again toggles closed. Single
// confirm step matches Slack/Discord-style remove-user UX without the
// native browser dialog.
export function openKickConfirm(anchor: HTMLElement, onConfirm: () => void): void {
  if (openMenuAnchor === anchor) {
    closeMessageMenu();
    return;
  }
  closeMessageMenu();

  const menu = document.createElement("div");
  menu.className = "msg-menu msg-menu-confirm";
  menu.setAttribute("role", "menu");

  const yes = document.createElement("button");
  yes.type = "button";
  yes.className = "msg-menu-icon-btn msg-menu-icon-btn-danger";
  yes.setAttribute("aria-label", "Confirm remove from room");
  yes.title = "Confirm remove from room";
  yes.innerHTML = `
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <polyline points="5 12 10 17 19 7" />
    </svg>`;
  menu.append(yes);

  document.body.appendChild(menu);
  openMenuEl = menu;
  openMenuAnchor = anchor;
  anchor.setAttribute("aria-expanded", "true");
  positionMenuRelativeTo(menu, anchor, "center");

  const submit = () => {
    onConfirm();
    closeMessageMenu();
  };
  yes.addEventListener("click", (ev) => {
    ev.stopPropagation();
    submit();
  });
  menu.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && !ev.isComposing) {
      ev.preventDefault();
      ev.stopPropagation();
      submit();
    }
  });
  yes.focus();
}

// Number of color slots defined in CSS (.who-c0 ... .who-cN-1). Keep in
// sync with style.css.
const AUTHOR_COLOR_SLOTS = 16;

// First-come-first-served slot allocation, scoped to this client's view.
// Authors take the lowest unused slot on first sight, so the first
// AUTHOR_COLOR_SLOTS distinct authors at any one time are guaranteed
// unique colors. releaseAuthorColor frees a slot when a peer leaves so a
// later joiner can take it — keeps churning rooms collision-free. Only
// when more than AUTHOR_COLOR_SLOTS authors are *simultaneously* in the
// room do we fall back to hashing.
const authorSlots = new Map<string, number>();

function authorColorIndex(from: string): number {
  const cached = authorSlots.get(from);
  if (cached !== undefined) return cached;
  if (authorSlots.size < AUTHOR_COLOR_SLOTS) {
    const used = new Set(authorSlots.values());
    for (let i = 0; i < AUTHOR_COLOR_SLOTS; i++) {
      if (!used.has(i)) {
        authorSlots.set(from, i);
        return i;
      }
    }
  }
  // Slots exhausted — degrade to FNV-1a over the peer ID. Cache the
  // result so the same author keeps the same color on later messages.
  let h = 0x811c9dc5;
  for (let i = 0; i < from.length; i++) {
    h ^= from.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const slot = (h >>> 0) % AUTHOR_COLOR_SLOTS;
  authorSlots.set(from, slot);
  return slot;
}

// releaseAuthorColor frees the slot held by `from` so a later joiner can
// claim it. Old chat messages from `from` keep their original .who-cN
// class on the DOM, so they continue rendering in the original color
// until those nodes are removed; only NEW messages from a peer that
// later inherits this slot will show in the reused color, and those
// messages display under the new peer's name. No-op if `from` never
// took a slot (e.g., a peer who never spoke).
export function releaseAuthorColor(from: string): void {
  authorSlots.delete(from);
}

// renameChatAuthor rewrites the displayed name on every existing chat
// message authored by `from`. Continuation rows hide the name via CSS, so
// this is a no-op visually for those.
export function renameChatAuthor(from: string, name: string): void {
  const list = document.querySelector<HTMLElement>("#chat-messages");
  if (!list) return;
  for (const el of list.querySelectorAll<HTMLElement>(`.msg[data-from="${cssEscape(from)}"]`)) {
    const who = el.querySelector<HTMLElement>(".who");
    if (who) who.textContent = name;
  }
}

function cssEscape(s: string): string {
  // peer IDs are 16 hex chars from the server, but accept arbitrary input
  // defensively. Use the standard helper when available; otherwise fall
  // back to a conservative escape that handles the characters CSS needs.
  type CSSWithEscape = typeof CSS & { escape?: (s: string) => string };
  const fn = (CSS as CSSWithEscape).escape;
  if (typeof fn === "function") return fn.call(CSS, s);
  return s.replace(/([^a-zA-Z0-9_-])/g, "\\$1");
}

export function appendChatSystem(text: string): void {
  const tpl = document.querySelector<HTMLTemplateElement>("#chat-system");
  if (!tpl) throw new Error("chat-system template missing");
  const node = tpl.content.firstElementChild!.cloneNode(true) as HTMLLIElement;
  node.textContent = text;
  appendChatNode(node);
}

export function clearChat(): void {
  const list = document.querySelector<HTMLElement>("#chat-messages");
  list?.replaceChildren();
}

function appendChatNode(node: HTMLLIElement): void {
  const list = document.querySelector<HTMLElement>("#chat-messages");
  if (!list) return;

  // Group consecutive messages from the same author by tagging this node
  // .continued — a system message between two messages from the same
  // author breaks the group. CSS hides the .who and .time on .continued
  // rows so the body flows as a paragraph.
  if (node.classList.contains("msg") && !node.classList.contains("system")) {
    const prev = list.lastElementChild as HTMLElement | null;
    if (prev && prev.classList.contains("msg") && !prev.classList.contains("system")) {
      const prevFrom = prev.dataset.from;
      const thisFrom = node.dataset.from;
      if (prevFrom && thisFrom && prevFrom === thisFrom) {
        node.classList.add("continued");
      }
    }
  }

  // "Near bottom" tolerance for sticky autoscroll. We can't always pin
  // (the user might be reading history) — only do it when they're close
  // enough to the tail that scroll movement won't be disorienting.
  const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
  const stick = distance < 80;
  list.appendChild(node);
  if (stick) list.scrollTop = list.scrollHeight;
}

// PresenterView controls the screen-share viewer plus the rail indicator
// that opens it. Two states are tracked independently:
//
//   - Active: there is a presenter (someone, possibly us). The rail
//     indicator shows their name; clicking it opens the pane.
//   - Open: the pane is rendering the stream. The chat column collapses.
//
// Pane open/close is user-driven (via the indicator and the close button).
// The pane never auto-opens on a new presenter — the indicator is the
// single entry point so a remote share never takes over the chat by
// surprise.
export interface PresenterInfo {
  name: string;
  stream: MediaStream | null;
  isSelf: boolean;
}
export interface PresenterView {
  // setActive records who the current presenter is (or null when nobody).
  // The indicator visibility and label follow from this; the pane updates
  // its content when open. Passing stream:null keeps the indicator visible
  // but the pane will render nothing until the stream arrives.
  setActive: (info: PresenterInfo | null) => void;
  // setOpen toggles the pane on/off. Independent of setActive — the pane
  // can be closed while a presenter is still active (indicator stays).
  setOpen: (open: boolean) => void;
  isOpen: () => boolean;
}
export function createPresenterView(opts: {
  onStopShare: () => void;
  onClose: () => void;
  // onAudioVolumeChange fires when the user moves the volume slider or
  // toggles the mute icon in the overlay. The value is a linear gain
  // factor in [0, 2] (0 = silent, 1 = unity, 2 = +6 dB boost). The view
  // tracks the value internally for icon state; the caller is responsible
  // for the actual playback gating (typically setVolume on the
  // AudioContext-routed audio handle).
  onAudioVolumeChange: (volume: number) => void;
}): PresenterView {
  const pane = document.querySelector<HTMLElement>("#presenter-pane");
  const video = document.querySelector<HTMLVideoElement>("#presenter-video");
  const stopBtn = document.querySelector<HTMLButtonElement>("#presenter-stop");
  const closeBtn = document.querySelector<HTMLButtonElement>("#presenter-close");
  const fsBtn = document.querySelector<HTMLButtonElement>("#presenter-fullscreen");
  const audioGroup = document.querySelector<HTMLElement>("#presenter-audio");
  const muteBtn = document.querySelector<HTMLButtonElement>("#presenter-audio-mute");
  const volSlider = document.querySelector<HTMLInputElement>("#presenter-audio-vol");
  const indicator = document.querySelector<HTMLButtonElement>("#presenter-indicator");
  const indicatorName = document.querySelector<HTMLElement>("#presenter-indicator-name");
  if (!pane || !video || !stopBtn || !closeBtn || !fsBtn || !audioGroup || !muteBtn || !volSlider || !indicator || !indicatorName) {
    throw new Error("presenter UI elements missing");
  }

  // iPhone Safari supports element-level fullscreen only on <video> elements;
  // calling requestFullscreen on the wrapping div silently does nothing.
  // Hide the button rather than ship a dead control.
  if (!document.fullscreenEnabled) {
    fsBtn.hidden = true;
  }

  let active: PresenterInfo | null = null;
  let open = false;
  // volume is the current gain factor in [0, 2]. lastNonZero remembers
  // the level before the user muted, so unmuting via the icon restores
  // it instead of jumping back to 1.0.
  let volume = 1;
  let lastNonZero = 1;
  // Per-stream listeners for addtrack/removetrack so the volume cluster
  // reflects whether the active stream actually carries audio. When a
  // stream is replaced (or cleared), we detach these.
  let detachStreamListeners: (() => void) | null = null;

  const refreshAudioVisibility = () => {
    // The volume control only makes sense for remote streams that carry
    // audio. Self-preview never plays remote audio (we don't pipe our
    // own captured screen audio back) so the cluster stays hidden then.
    const hasAudio = !!active?.stream && active.stream.getAudioTracks().length > 0;
    const show = hasAudio && !!active && !active.isSelf;
    audioGroup.hidden = !show;
  };

  const applyVolume = (v: number, source: "icon" | "slider" | "reset") => {
    volume = Math.max(0, Math.min(2, v));
    if (volume > 0) lastNonZero = volume;
    const muted = volume === 0;
    muteBtn.classList.toggle("is-muted", muted);
    muteBtn.title = muted ? "Unmute screen audio" : "Mute screen audio";
    muteBtn.setAttribute("aria-label", muteBtn.title);
    // Avoid a feedback loop when the slider's own input event triggered
    // this — its value already reflects the new state.
    if (source !== "slider") volSlider.value = String(Math.round(volume * 100));
    if (source !== "reset") opts.onAudioVolumeChange(volume);
  };

  const watchStreamTracks = (stream: MediaStream | null) => {
    detachStreamListeners?.();
    detachStreamListeners = null;
    if (!stream) return;
    const onChange = () => refreshAudioVisibility();
    stream.addEventListener("addtrack", onChange);
    stream.addEventListener("removetrack", onChange);
    detachStreamListeners = () => {
      stream.removeEventListener("addtrack", onChange);
      stream.removeEventListener("removetrack", onChange);
    };
  };

  const renderPane = () => {
    if (!active) {
      video.srcObject = null;
      stopBtn.hidden = true;
      return;
    }
    stopBtn.hidden = !active.isSelf;
    // Re-assigning the same stream is a no-op; a different one swaps
    // cleanly. The video stays muted (autoplay-friendly + no echo for
    // self); remote system audio is wired separately by main.ts via
    // attachRemoteStream so the AudioContext output device applies.
    if (video.srcObject !== active.stream) {
      video.srcObject = active.stream;
      void video.play().catch(() => { /* autoplay edge cases */ });
    }
  };

  const renderOpen = () => {
    pane.hidden = !open;
    document.body.classList.toggle("presenter-open", open);
    if (open) renderPane();
  };

  const renderIndicator = () => {
    if (!active) {
      indicator.hidden = true;
      indicatorName.textContent = "";
      return;
    }
    indicator.hidden = false;
    indicatorName.textContent = active.isSelf ? "You're sharing" : active.name;
  };

  // Wire the indicator: clicking opens (or closes if already open).
  indicator.addEventListener("click", () => {
    open = !open;
    renderOpen();
  });
  closeBtn.addEventListener("click", () => {
    open = false;
    renderOpen();
    opts.onClose();
  });
  stopBtn.addEventListener("click", () => opts.onStopShare());
  muteBtn.addEventListener("click", () => {
    applyVolume(volume === 0 ? lastNonZero : 0, "icon");
  });
  volSlider.addEventListener("input", () => {
    applyVolume(Number(volSlider.value) / 100, "slider");
  });
  fsBtn.addEventListener("click", () => {
    type WithFS = HTMLElement & {
      requestFullscreen?: () => Promise<void>;
    };
    type DocWithFS = Document & {
      fullscreenElement?: Element | null;
      exitFullscreen?: () => Promise<void>;
    };
    const doc = document as DocWithFS;
    if (doc.fullscreenElement) {
      void doc.exitFullscreen?.();
    } else {
      void (pane as WithFS).requestFullscreen?.().catch((err) => {
        console.warn("requestFullscreen failed", err);
      });
    }
  });

  return {
    setActive: (info) => {
      // Reset volume to unity on any presenter change — fresh stream
      // gets a fresh default. Saves the user from inheriting a volume
      // tweak (or a mute) they don't remember setting. We pass "reset"
      // so this doesn't re-fire onAudioVolumeChange; the caller
      // re-attaches a fresh AudioContext gain node which already starts
      // at 1.0.
      const presenterChanged = !active || !info
        || active.stream !== info.stream
        || active.isSelf !== info.isSelf;
      if (presenterChanged) {
        lastNonZero = 1;
        applyVolume(1, "reset");
      }
      active = info;
      if (presenterChanged) watchStreamTracks(info?.stream ?? null);
      refreshAudioVisibility();
      renderIndicator();
      // If the pane is already open and the active presenter goes away,
      // close it; otherwise update content in place.
      if (!info && open) {
        open = false;
        renderOpen();
      } else if (open) {
        renderPane();
      }
    },
    setOpen: (next) => {
      open = next;
      renderOpen();
    },
    isOpen: () => open,
  };
}

// ScreenShareButton wraps the header share button: visibility, sharing
// state, and a tiny popover that asks the user to pick a mode before
// opening getDisplayMedia. The button is never disabled while a remote
// peer is sharing — clicking starts a takeover.
export interface ScreenShareButton {
  setVisible: (v: boolean) => void;
  setSharing: (sharing: boolean) => void;
}
export function bindScreenShareButton(opts: {
  onPick: (mode: "document" | "motion") => void;
  onStop: () => void;
}): ScreenShareButton {
  const btn = document.querySelector<HTMLButtonElement>("#screen-share");
  const pop = document.querySelector<HTMLElement>("#share-picker");
  const docBtn = document.querySelector<HTMLButtonElement>("#share-document");
  const motBtn = document.querySelector<HTMLButtonElement>("#share-motion");
  if (!btn || !pop || !docBtn || !motBtn) {
    throw new Error("screen-share UI elements missing");
  }
  let sharing = false;

  const setOpen = (open: boolean) => {
    pop.hidden = !open;
    btn.setAttribute("aria-expanded", open ? "true" : "false");
  };

  btn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    if (sharing) {
      opts.onStop();
      return;
    }
    setOpen(pop.hidden);
  });

  docBtn.addEventListener("click", () => {
    setOpen(false);
    opts.onPick("document");
  });
  motBtn.addEventListener("click", () => {
    setOpen(false);
    opts.onPick("motion");
  });

  document.addEventListener("click", (ev) => {
    if (pop.hidden) return;
    const target = ev.target as Node | null;
    if (target && (pop.contains(target) || btn.contains(target))) return;
    setOpen(false);
  });
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && !pop.hidden) {
      setOpen(false);
      btn.focus();
    }
  });

  return {
    setVisible: (v) => { btn.hidden = !v; },
    setSharing: (s) => {
      sharing = s;
      btn.classList.toggle("is-sharing", s);
      btn.title = s ? "Stop sharing" : "Share screen";
      btn.setAttribute("aria-label", btn.title);
      if (s) setOpen(false);
    },
  };
}

// MicLevelMeter draws a small bar that follows the live mic input, used in
// the lobby so users can verify their selected mic before joining.
export interface MicLevelMeter {
  start: (track: MediaStreamTrack) => void;
  stop: () => void;
}
export function createMicLevelMeter(barEl: HTMLElement): MicLevelMeter {
  let stop: (() => void) | null = null;
  return {
    start: (track) => {
      stop?.();
      const release = observeMicLevel(track, (level) => {
        barEl.style.width = `${level * 100}%`;
      });
      stop = () => {
        release();
        barEl.style.width = "0%";
      };
    },
    stop: () => {
      stop?.();
      stop = null;
    },
  };
}
